import React, { useState, useRef } from 'react';
import { Star, X, Image as ImageIcon, Video as VideoIcon } from 'lucide-react';
import axios from 'axios';
import { useNotifications } from "../Notification/UseNotifications.tsx";
import {uploadFileToApi} from "../../api/filesApi.ts";
import styles from './ReviewForm.module.scss';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

interface FilePreview {
    file: File;
    previewUrl: string;
    type: 'image' | 'video';
}

const ReviewForm = () => {
    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(0);
    const [previews, setPreviews] = useState<FilePreview[]>([]);
    const [name, setName] = useState('');
    const [content, setContent] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const imageInputRef = useRef<HTMLInputElement>(null);
    const videoInputRef = useRef<HTMLInputElement>(null);

    const { addNotification } = useNotifications();

    const showSuccessNotification = (message: string) => {
        addNotification({
            type: 'success',
            title: 'Успешно',
            message,
            duration: 5000
        });
    };

    const showErrorNotification = (message: string) => {
        addNotification({
            type: 'error',
            title: 'Ошибка',
            message,
            duration: 5000
        });
    };
// Обработка загрузки файлов
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'video') => {
        if (e.target.files && e.target.files.length > 0) {
            const newFiles = Array.from(e.target.files).map(file => ({
                file,
                previewUrl: URL.createObjectURL(file),
                type
            }));
            setPreviews(prev => [...prev, ...newFiles]);
        }
        // Сбрасываем value инпута, чтобы можно было загрузить тот же файл повторно, если его удалили
        e.target.value = '';
    };

    // Удаление файла
    const removeFile = (indexToRemove: number) => {
        setPreviews(prev => {
            const updated = prev.filter((_, index) => index !== indexToRemove);
            // Освобождаем память URL удаленного файла
            URL.revokeObjectURL(prev[indexToRemove].previewUrl);
            return updated;
        });
    };

    // Загрузка всех файлов на TimeWeb S3
    const uploadFiles = async (): Promise<{ images: string[]; videos: string[] }> => {
        const uploadedImages: string[] = [];
        const uploadedVideos: string[] = [];

        for (const preview of previews) {
            try {
                const {url} = await uploadFileToApi(preview.file, {folder: "reviews"});
                if (preview.type === 'image') {
                    uploadedImages.push(url);
                } else {
                    uploadedVideos.push(url);
                }
            } catch (error: any) {
                throw new Error(`Ошибка загрузки файла ${preview.file.name}: ${error.message}`);
            }
        }

        return { images: uploadedImages, videos: uploadedVideos };
    };

    // Отправка отзыва
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Валидация
        if (!name.trim()) {
            showErrorNotification('Пожалуйста, введите ваше имя');
            return;
        }

        if (rating === 0) {
            showErrorNotification('Пожалуйста, поставьте оценку');
            return;
        }

        if (!content.trim()) {
            showErrorNotification('Пожалуйста, напишите текст отзыва');
            return;
        }

        setIsSubmitting(true);

        try {
            // Загружаем файлы
            let images: string[] = [];
            let videos: string[] = [];

            if (previews.length > 0) {
                const uploadResult = await uploadFiles();
                images = uploadResult.images;
                videos = uploadResult.videos;
            }

            // Отправляем данные отзыва
            const reviewData = {
                name: name.trim(),
                rating,
                content: content.trim(),
                status: 'pending',
                images,
                videos,
                avatar: null,
                project_id: null
            };

            // Отправляем на сервер с оберткой в review
            await axios.post(`${API_BASE_URL}/api/v1/reviews`, {
                review: reviewData  // <-- Обернули в review
            }, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            // Очищаем форму
            setName('');
            setRating(0);
            setHover(0);
            setContent('');

            // Освобождаем память от preview URL
            previews.forEach(preview => {
                URL.revokeObjectURL(preview.previewUrl);
            });
            setPreviews([]);

            showSuccessNotification('Спасибо! Ваш отзыв успешно отправлен и будет опубликован после проверки.');

        } catch (error: any) {
            console.error('Error submitting review:', error);
            const errorMessage = error.response?.data?.message ||
                error.message ||
                'Произошла ошибка при отправке отзыва. Пожалуйста, попробуйте еще раз.';
            showErrorNotification(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={styles.reviewWrapper}>
            <div className={styles.reviewContainer}>
                <div className={styles.header}>
                    <h3 className={styles.title}>Оставьте отзыв</h3>
                    <p className={styles.description}>
                        Поделитесь впечатлениями и прикрепите фото или видео результата.
                    </p>
                </div>

                <form className={styles.form} onSubmit={handleSubmit}>
                    {/* Имя */}
                    <div className={styles.field}>
                        <label htmlFor="review-name" className={styles.label}>Ваше имя*</label>
                        <input
                            id="review-name"
                            type="text"
                            className={styles.input}
                            placeholder="Представьтесь, пожалуйста"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={isSubmitting}
                        />
                    </div>

                    {/* Оценка */}
                    <div className={styles.field}>
                        <label className={styles.label}>Ваша оценка*</label>
                        <div
                            className={styles.starsWrapper}
                            onMouseLeave={() => setHover(0)}
                        >
                            {[...Array(5)].map((_, index) => {
                                const ratingValue = index + 1;
                                const isFilled = ratingValue <= (hover || rating);
                                return (
                                    <button
                                        type="button"
                                        key={index}
                                        className={styles.starBtn}
                                        onClick={() => !isSubmitting && setRating(ratingValue)}
                                        onMouseEnter={() => !isSubmitting && setHover(ratingValue)}
                                        disabled={isSubmitting}
                                        aria-label={`Оценка ${ratingValue}`}
                                    >
                                        <Star
                                            size={32}
                                            className={isFilled ? styles.starFilled : styles.starEmpty}
                                            fill={isFilled ? "#f59e0b" : "none"}
                                        />
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Текст */}
                    <div className={styles.field}>
                        <label htmlFor="review-text" className={styles.label}>Текст отзыва*</label>
                        <textarea
                            id="review-text"
                            className={styles.textarea}
                            placeholder="Расскажите, что вам понравилось..."
                            rows={5}
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            disabled={isSubmitting}
                        />
                    </div>

                    {/* Загрузка медиа */}
                    <div className={styles.mediaSection}>
                        <label className={styles.label}>Медиафайлы (необязательно)</label>
                        <div className={styles.uploadButtons}>
                            <button
                                type="button"
                                className={styles.uploadBtn}
                                onClick={() => !isSubmitting && imageInputRef.current?.click()}
                                disabled={isSubmitting}
                            >
                                <ImageIcon size={20} />
                                <span>Добавить фото</span>
                            </button>
                            <input
                                ref={imageInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                hidden
                                onChange={(e) => handleFileChange(e, 'image')}
                                disabled={isSubmitting}
                            />

                            <button
                                type="button"
                                className={styles.uploadBtn}
                                onClick={() => !isSubmitting && videoInputRef.current?.click()}
                                disabled={isSubmitting}
                            >
                                <VideoIcon size={20} />
                                <span>Добавить видео</span>
                            </button>
                            <input
                                ref={videoInputRef}
                                type="file"
                                accept="video/*"
                                multiple
                                hidden
                                onChange={(e) => handleFileChange(e, 'video')}
                                disabled={isSubmitting}
                            />
                        </div>

                        {/* Предпросмотр файлов */}
                        {previews.length > 0 && (
                            <div className={styles.previewsGrid}>
                                {previews.map((item, index) => (
                                    <div key={index} className={styles.previewItem}>
                                        {item.type === 'image' ? (
                                            <img src={item.previewUrl} alt="Preview" className={styles.previewMedia} />
                                        ) : (
                                            <video src={item.previewUrl} className={styles.previewMedia} controls />
                                        )}
                                        <button
                                            type="button"
                                            className={styles.removeBtn}
                                            onClick={() => !isSubmitting && removeFile(index)}
                                            disabled={isSubmitting}
                                            title="Удалить файл"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <button
                        type="submit"
                        className={styles.submitButton}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Отправка...' : 'Отправить отзыв'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ReviewForm;
